import React from 'react';
import { Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  points: number;
  tier: string;
  nextTier: string | null;
  pointsToNextTier: number;
  progressPct: number;
}

const TIER_COLORS: Record<string, { bg: string; text: string; border: string; icon: keyof typeof Ionicons.glyphMap }> = {
  Bronze: { bg: 'rgba(205, 127, 50, 0.18)', text: '#E5A663', border: 'rgba(205, 127, 50, 0.4)', icon: 'shield-outline' },
  Silver: { bg: 'rgba(192, 192, 192, 0.18)', text: '#E2E8F0', border: 'rgba(192, 192, 192, 0.4)', icon: 'ribbon-outline' },
  Gold: { bg: 'rgba(230, 177, 92, 0.22)', text: '#F6C358', border: 'rgba(230, 177, 92, 0.5)', icon: 'trophy-outline' },
  Platinum: { bg: 'rgba(167, 139, 250, 0.2)', text: '#C4B5FD', border: 'rgba(167, 139, 250, 0.4)', icon: 'diamond-outline' },
};

export function LoyaltyCard({ points, tier, nextTier, pointsToNextTier, progressPct }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors, isDark } = useAppTheme();
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.Bronze;

  return (
    <View style={[styles.card, { backgroundColor: isDark ? colors.surface : '#0F172A', borderColor: isDark ? colors.borderStrong : 'rgba(230, 177, 92, 0.28)' }]}>
      <View style={[styles.shineStrip, { backgroundColor: colors.accentFill }]} />

      <View style={styles.topRow}>
        <View style={styles.balanceCol}>
          <Text style={[type.overline, styles.label]}>{trText('LOYALTY BALANCE', 'লয়্যালটি ব্যালেন্স')}</Text>
          <View style={styles.pointsRow}>
            <Text style={[type.display, styles.points]} numberOfLines={1} adjustsFontSizeToFit>{points.toLocaleString()}</Text>
            <Text style={[styles.ptsUnit, { color: colors.accent }]}>{trText('pts', 'পয়েন্ট')}</Text>
          </View>
        </View>
        <View style={[styles.tierBadge, { backgroundColor: tierStyle.bg, borderColor: tierStyle.border }]}>
          <Ionicons name={tierStyle.icon} size={14} color={tierStyle.text} />
          <Text style={[styles.tierText, { color: tierStyle.text }]}>{trText(`${tier} Member`, `${tier} মেম্বার`)}</Text>
        </View>
      </View>

      <View style={styles.progressSection}>
        <View style={styles.progressLabelRow}>
          <Text style={[type.caption, styles.progressText]}>
            {nextTier ? trText(`${pointsToNextTier.toLocaleString()} pts to reach ${nextTier}`, `${nextTier}-এ পৌঁছাতে আর ${pointsToNextTier.toLocaleString()} পয়েন্ট`) : trText('Top loyalty tier reached!', 'আপনি ম্যাক্সিমাম টিয়ারে পৌঁছে গেছেন!')}
          </Text>
          <Text style={[type.caption, styles.progressPctText, { color: colors.accent }]}>{progressPct}%</Text>
        </View>
        <View style={styles.progressTrack}>
          <View style={[styles.progressFill, { width: `${Math.min(100, Math.max(progressPct, 4))}%`, backgroundColor: colors.accentFill }]} />
        </View>
      </View>
    </View>
  );
}

const styles = ScaledSheet.create({
  card: {
    backgroundColor: colors.ink,
    borderRadius: radius.xl,
    padding: spacing.xl,
    gap: spacing.lg,
    borderWidth: 1,
    borderColor: 'rgba(230, 177, 92, 0.28)',
    overflow: 'hidden',
    position: 'relative',
    ...shadows.elevated,
  },
  shineStrip: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: colors.accentFill,
  },
  // The tier badge drops under the points on a narrow phone rather than squeezing them.
  topRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm },
  label: { color: colors.textOnDarkSecondary, letterSpacing: 1 },
  pointsRow: { flexDirection: 'row', alignItems: 'baseline', gap: 6, marginTop: 4 },
  points: { color: colors.textOnDark, flexShrink: 1 },
  balanceCol: { flexShrink: 1 },
  ptsUnit: { color: colors.accent, fontSize: 18, fontWeight: '700' },
  tierBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexShrink: 1,
  },
  tierText: { fontSize: 12, fontWeight: '800', flexShrink: 1 },
  progressSection: { gap: spacing.xs },
  progressLabelRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  progressText: { color: colors.textOnDarkSecondary },
  progressPctText: { color: colors.accent, fontWeight: '700' },
  progressTrack: { height: 7, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.12)', overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: colors.accentFill, borderRadius: 4 },
});
